import { LoadingOutlined } from '@ant-design/icons'
import bwipjs from 'bwip-js'
import React from 'react'
import { format, secondsToMinSec } from '@wolf/dates'
import { getTranslations } from './TranslationTemplate'
import { AllergensBlock } from './shared/AllergensBlock'
import { IngredientsBlock } from './shared/IngredientsBlock'
import { NutritionTable } from './shared/NutritionTable'
import { PdfFrame } from '../../../../components/labels/PdfFrame'
import { usePageOutletContext } from '../../../../page-outlet-context'
import { trpc } from '../../../../utils/trpc'

type Props = {
  productId: number
  expirationDate: Date
  quantity: number
  onClose: () => void
}

export const RetailLabelTemplate: React.FC<Props> = ({
  productId,
  expirationDate,
  quantity,
  onClose,
}) => {
  const { sourceFilter, notificationInstance } = usePageOutletContext()
  const t = getTranslations(sourceFilter.language)

  const showError = (errorMessage: string) =>
    notificationInstance.error({
      message: 'Error en formulario',
      description: errorMessage,
    })

  const productQuery = trpc.products.getRetailProductById.useQuery(
    { productId, sourceId: sourceFilter.serialId },
    {
      retry: false,
      onError: (error) => {
        showError(error.message)
        onClose()
      },
    },
  )

  if (productQuery.isLoading) return <LoadingOutlined />
  if (!productQuery.data) return <></>

  const { data: product } = productQuery

  const lotId = format(new Date(), 'ddMMyy')

  const barcodeImage = bwipjs
    .toCanvas(document.createElement('canvas'), {
      bcid: 'ean13',
      text: product.gs1Code,
      height: 14,
    })
    .toDataURL('image/png')

  const contentArray = new Array(quantity).fill(0)

  return (
    <PdfFrame onClose={onClose} waitingTime={10000}>
      <link type="text/css" rel="Stylesheet" href="/styles/RetailLabelTemplate.css" />
      {contentArray.map((_, index) => (
        <div className="content" key={index}>
          <div className="name-dish-top">
            <strong>{product.name}</strong>
          </div>

          <div className="column">
            <div className="column1">
              <div className="micro">
                {product.tuppers.map((tupper) => (
                  <div className="micro1" key={tupper.name}>
                    <img className="micro-img" src={`/images/${tupper.cooking !== 0 ? 'heat' : 'noheat'}.svg`} />
                    <div className="micro-text">
                      <p className="micro-time">
                        {tupper.cooking !== 0 ? secondsToMinSec(tupper.cooking) : t.notHeat}
                      </p>
                      {product.tuppers.length > 1 && <p className="micro-name">{tupper.label ?? tupper.name}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="column2 topcolumn">
              <p className="logo">
                <img className="logo" src="/images/pan.svg" />
              </p>
            </div>
          </div>

          <div className="column">
            <div className="column1">
              <IngredientsBlock productName={product.name} ingredients={product.ingredients} t={t} />
              <NutritionTable weight={product.weight} nutritionFacts={product.nutritionFacts} t={t} />
            </div>

            <div className="column2 column2-left">
              <div>
                <p className="date-text">{t.expirationDate}:</p>
                <p className="date">{format(expirationDate, 'dd/MM/yyyy')}</p>
                <p className="batch">Lote: {lotId}</p>
                <p className="date-information">{t.protectAtmosphere}</p>
              </div>

              <AllergensBlock allergens={product.allergens} t={t} />

              <div className="code">
                <div className="code-icon">
                  <img width={120} src={barcodeImage} />
                  <p className="code-icon-text">{product.gs1Code}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </PdfFrame>
  )
}
